import Vue from 'vue';
import { assign, find, get } from 'lodash';
import * as api from 'api/api-programs';
import * as types from '../mutation-types';

const initState = {
  ready: false,
  fetching: false,
  fetched: true,
  saving: false,
  saveError: null,
  saved: null,
  error: null,
  notFound: null,
  transition: null,
  meta: {},
  data: {},
};

const neutralState = {
  fetching: false,
  fetched: true,
  saving: false,
  saveError: null,
  saved: null,
  error: null,
  notFound: null,
  transition: null,
};

const state = {};

const getters = {
  ProgramDetails: state => id => state[id],
};

const actions = {
  GetProgramDetails({ commit }, params) {
    const id = params.id;
    const type = params.type;
    commit(types.FETCH_PROGRAM_DETAILS_START, { id });
    api.getDetails(
      type,
      id,
      (response) => {
        commit(types.RECEIVE_PROGRAM_DETAILS, { id, response });
      },
      (description, status, error) => {
        if (status === 404) {
          commit(types.FETCH_PROGRAM_DETAILS_NOT_FOUND, { id });
        } else {
          commit(types.FETCH_PROGRAM_DETAILS_ERROR, { id, error });
        }
      },
    );
  },
  CreateProgramDetailsInit({ commit }, params) {
    const id = params.id;
    commit(types.CREATE_PROGRAM_DETAILS_INIT, { id });
  },
  CreateProgramDetails({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const payload = params.payload;

      commit(types.CREATE_PROGRAM_DETAILS_START, { id });
      console.log('create program data', payload);
      api.createProgramDetails(
        type,
        payload,
        (response) => {
          console.log('save');
          commit(types.CREATE_PROGRAM_DETAILS_SUCCESSFUL, { id, response });
          resolve(true);
        },
        (error, status, saveError) => {
          console.log('error', status, saveError);
          commit(types.CREATE_PROGRAM_DETAILS_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  SaveProgramDetails({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const payload = params.payload;

      commit(types.SAVE_PROGRAM_DETAILS_START, { id });
      console.log('save data', payload);
      api.saveDetails(
        type,
        id,
        payload,
        (response) => {
          console.log('save');
          commit(types.SAVE_PROGRAM_DETAILS_SUCCESSFUL, { id, response });
          resolve(resolve);
        },
        (error, status, saveError) => {
          console.log('error', status, saveError);
          commit(types.SAVE_PROGRAM_DETAILS_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  ManageProgramDays({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const payload = params.payload;
      commit(types.MANAGE_PROGRAM_DAYS_START, { id });
      api.manageDays(
        type,
        id,
        payload,
        (response) => {
          commit(types.MANAGE_PROGRAM_DAYS_SUCCESSFUL, { id, response });
          dispatch('UpdateProgramDay', response);
          resolve(true);
        },
        (error, status, saveError) => {
          commit(types.MANAGE_PROGRAM_DAYS_ERROR, { id, saveError });
          reject(saveError);
        },
      );
    });
  },
  CopyProgramDay({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const dayID = params.dayID;
      const payload = params.payload;
      commit(types.COPY_PROGRAM_DAY_START, { id });

      api.copyDay(
        type,
        id,
        dayID,
        payload,
        (response) => {
          commit(types.COPY_PROGRAM_DAY_SUCCESSFUL, { id, response });
          const day = find(response.data, item => item.day_number === payload.day_number);
          resolve(day);
        },
        (error, status, saveError) => {
          commit(types.COPY_PROGRAM_DAY_ERROR, { id, saveError });
          reject(saveError);
        },
      );
    });
  },
  SaveProgramCoverImage({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const file = params.file;

      commit(types.SAVE_PROGRAM_COVER_IMAGE_START, { id });
      api.uploadCoverImage(
        type,
        id,
        file,
        (response) => {
          commit(types.SAVE_PROGRAM_COVER_IMAGE_DONE, { id, response });
          resolve(true);
        },
        (error, status, saveError) => {
          commit(types.SAVE_PROGRAM_COVER_IMAGE_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  RemoveProgramCoverImage({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;

      commit(types.REMOVE_PROGRAM_COVER_IMAGE_START, { id });
      api.removeCoverImage(
        type,
        id,
        () => {
          commit(types.REMOVE_PROGRAM_COVER_IMAGE_DONE, { id });
          resolve(true);
        },
        (error, status, saveError) => {
          commit(types.REMOVE_PROGRAM_COVER_IMAGE_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  DeleteProgram({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;

      const payload = params.payload || {};

      commit(types.DELETE_PROGRAM_START, { id });
      api.deleteProgram(
        type,
        id,
        payload,
        () => {
          commit(types.DELETE_PROGRAM_DONE, { id });
          resolve(true);
        },
        (error, status, saveError) => {
          commit(types.DELETE_PROGRAM_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  ProgramStatusTransition({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const transition = params.transition;
      const payload = params.payload;

      // 'accept' 'mark-as-ready' 'activate' 'put-on-hold' 'reactivate' 'cancel' 'mark-as-draft'

      commit(types.PROGRAM_STATUS_TRANSITION_START, { id });
      api.startProgramStatusTransition(
        type,
        id,
        transition,
        payload,
        (response) => {
          commit(types.PROGRAM_STATUS_TRANSITION_DONE, { id, response, transition });
          resolve(true);
        },
        (error, status, saveError) => {
          commit(types.PROGRAM_STATUS_TRANSITION_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  ProgramSendNotification({ commit }, params) {
    return new Promise((resolve, reject) => {
      const id = params.id;
      const type = params.type;
      const payload = params.payload;

      commit(types.PROGRAM_SEND_NOTIFICATION_START, { id });
      api.sendNotification(
        type,
        id,
        payload,
        (response) => {
          commit(types.PROGRAM_SEND_NOTIFICATION_DONE, { id, response });
          resolve(true);
        },
        (error, status, saveError) => {
          commit(types.PROGRAM_SEND_NOTIFICATION_ERROR, { id, saveError });
          reject(error);
        },
      );
    });
  },
  ClearProgramDetails({ commit }, { id }) {
    commit(types.CLEAR_PROGRAM_DETAILS_STORE, { id });
  },
  ClearProgramDetailsSaveError({ commit }, { id }) {
    commit(types.CLEAR_PROGRAM_DETAILS_SAVE_ERROR, { id });
  },
};

const mutations = {
  [types.FETCH_PROGRAM_DETAILS_START](state, { id }) {
    if (state[id]) Vue.set(state, id, assign(state[id], neutralState, { fetching: true }));
    else Vue.set(state, id, assign({}, initState, { fetching: true }));
  },
  [types.FETCH_PROGRAM_DETAILS_NOT_FOUND](state, { id }) {
    Vue.set(state, id, assign(state[id], initState, { notFound: true }));
  },
  [types.FETCH_PROGRAM_DETAILS_ERROR](state, { id, error }) {
    Vue.set(state, id, assign(state[id], neutralState, { error }));
  },
  [types.RECEIVE_PROGRAM_DETAILS](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState, { ready: true, meta: response.meta, data: response.data }));
  },

  [types.CLEAR_PROGRAM_DETAILS_STORE](state, { id }) {
    Vue.set(state, id, assign({}, initState));
  },

  [types.CLEAR_PROGRAM_DETAILS_SAVE_ERROR](state, { id }) {
    Vue.set(state, id, assign(state[id], { saveError: null }));
  },

  [types.CREATE_PROGRAM_DETAILS_INIT](state, { id }) {
    if (!state[id]) Vue.set(state, id, assign({}, initState, { ready: true }));
  },
  [types.CREATE_PROGRAM_DETAILS_START](state, { id }) {
    if (state[id]) Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
    else Vue.set(state, id, assign({}, initState, { saving: true }));
  },
  [types.CREATE_PROGRAM_DETAILS_SUCCESSFUL](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState, { ready: true, data: response.data, meta: response.meta }));
  },
  [types.CREATE_PROGRAM_DETAILS_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },

  [types.SAVE_PROGRAM_DETAILS_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.SAVE_PROGRAM_DETAILS_SUCCESSFUL](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState, { data: response.data, meta: response.meta }));
  },
  [types.SAVE_PROGRAM_DETAILS_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },

  [types.MANAGE_PROGRAM_DAYS_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.MANAGE_PROGRAM_DAYS_SUCCESSFUL](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState));
    Vue.set(state[id].data, 'duration', get(response, 'data.length'));
    Vue.set(state[id].data, 'ProgramDays', response.data);
  },
  [types.MANAGE_PROGRAM_DAYS_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },

  [types.COPY_PROGRAM_DAY_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.COPY_PROGRAM_DAY_SUCCESSFUL](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState));
    Vue.set(state[id].data, 'duration', get(response, 'data.length'));
    Vue.set(state[id].data, 'ProgramDays', response.data);
  },
  [types.COPY_PROGRAM_DAY_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },

  [types.SAVE_PROGRAM_COVER_IMAGE_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.SAVE_PROGRAM_COVER_IMAGE_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },
  [types.SAVE_PROGRAM_COVER_IMAGE_DONE](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState, { saved: true }));
    Vue.set(state[id].data, 'CoverImage', response.data);
  },

  [types.REMOVE_PROGRAM_COVER_IMAGE_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.REMOVE_PROGRAM_COVER_IMAGE_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },
  [types.REMOVE_PROGRAM_COVER_IMAGE_DONE](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { id, saved: true }));
    Vue.set(state[id].data, 'CoverImage', null);
  },

  [types.PROGRAM_STATUS_TRANSITION_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.PROGRAM_STATUS_TRANSITION_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },
  [types.PROGRAM_STATUS_TRANSITION_DONE](state, { id, response, transition }) {
    Vue.set(state, id, assign(state[id], neutralState, { saved: true, transition, data: response.data, meta: response.meta }));
  },

  [types.PROGRAM_SEND_NOTIFICATION_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.PROGRAM_SEND_NOTIFICATION_DONE](state, { id, response }) {
    Vue.set(state, id, assign(state[id], neutralState, { saved: true, data: response.data, meta: response.meta }));
  },
  [types.PROGRAM_SEND_NOTIFICATION_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },

  [types.DELETE_PROGRAM_START](state, { id }) {
    Vue.set(state, id, assign(state[id], neutralState, { saving: true }));
  },
  [types.DELETE_PROGRAM_ERROR](state, { id, saveError }) {
    Vue.set(state, id, assign(state[id], neutralState, { saveError }));
  },
  [types.DELETE_PROGRAM_DONE](state, { id }) {
    Vue.set(state, id, assign({}, initState));
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/programs/entities/program-details.js